import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { setBearerToken } from '../api/client';
import { refreshTokens, redirectToSignIn, signOut as cognitoSignOut } from './cognito';

export interface AdminIdentity {
  actorUserId: string;
  actorEmail: string;
}

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

export interface AuthContextValue {
  status: AuthStatus;
  actor: AdminIdentity | null;
  signIn: () => Promise<void>;
  signOut: () => void;
  setTokens: (idToken: string, refreshToken: string, expiresIn: number) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const E2E_MOCK = (import.meta.env?.VITE_E2E_MOCK_COGNITO as string | undefined) === '1';
const LOCAL_DEV = (import.meta.env?.VITE_LOCAL_DEV as string | undefined) === '1';
const REFRESH_TOKEN_KEY = 'cognito_refresh_token';

/**
 * Defense-in-depth: even when VITE_LOCAL_DEV=1 or VITE_E2E_MOCK_COGNITO=1
 * is baked into the bundle (which happens automatically when .env.local
 * lives on the build machine — Vite loads .env.local in ALL modes), we
 * MUST NOT skip Cognito on a real deployed host. Pin the bypass to
 * localhost / 127.0.0.1 only. Belt + suspenders so a stale env file on
 * an operator's laptop can't accidentally ship an auth-bypassed prod SPA.
 */
function isLocalDevHost(): boolean {
  if (typeof window === 'undefined' || !window.location) return false;
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1' || h === '0.0.0.0';
}

function shouldBypassAuth(): boolean {
  return (E2E_MOCK || LOCAL_DEV) && isLocalDevHost();
}

function parseActorFromIdToken(idToken: string): AdminIdentity {
  try {
    const payload = JSON.parse(atob(idToken.split('.')[1])) as {
      sub?: string;
      email?: string;
    };
    return {
      actorUserId: payload.sub ?? 'unknown',
      actorEmail: payload.email ?? 'unknown',
    };
  } catch {
    return { actorUserId: 'unknown', actorEmail: 'unknown' };
  }
}

interface TokenManagement {
  applyTokens: (idToken: string, refreshToken: string, expiresIn: number) => void;
  buildSignOut: () => () => void;
}

function useTokenManagement(
  setStatus: (s: AuthStatus) => void,
  setActor: (a: AdminIdentity | null) => void,
): TokenManagement {
  const idTokenRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function scheduleRefresh(refreshToken: string, expiresIn: number): void {
    if (timerRef.current) clearTimeout(timerRef.current);
    const delay = Math.max((expiresIn - 60) * 1000, 10_000);
    timerRef.current = setTimeout(() => {
      refreshTokens(refreshToken)
        .then(({ idToken, refreshToken: nr, expiresIn: ne }) => {
          applyTokens(idToken, nr, ne);
        })
        .catch(() => {
          setStatus('unauthenticated');
          setActor(null);
          idTokenRef.current = null;
          setBearerToken(null);
        });
    }, delay);
  }

  function applyTokens(idToken: string, refreshToken: string, expiresIn: number): void {
    idTokenRef.current = idToken;
    setBearerToken(idToken);
    sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    setActor(parseActorFromIdToken(idToken));
    setStatus('authenticated');
    scheduleRefresh(refreshToken, expiresIn);
  }

  function buildSignOut(): () => void {
    return () => {
      sessionStorage.removeItem(REFRESH_TOKEN_KEY);
      idTokenRef.current = null;
      setBearerToken(null);
      if (timerRef.current) clearTimeout(timerRef.current);
      cognitoSignOut();
    };
  }

  return { applyTokens, buildSignOut };
}

export function AuthProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [actor, setActor] = useState<AdminIdentity | null>(null);
  const { applyTokens, buildSignOut } = useTokenManagement(setStatus, setActor);

  useEffect(() => {
    if (shouldBypassAuth()) {
      // VITE_E2E_MOCK_COGNITO=1 (Playwright) or VITE_LOCAL_DEV=1 (pnpm admin-spa:local).
      // ALSO requires hostname === localhost — see isLocalDevHost() above
      // for why. Admin-api accepts "Bearer dev" when OUTLINKED_LOCAL=1 in
      // the container.
      setBearerToken('dev');
      setActor({ actorUserId: 'u_local_admin', actorEmail: 'admin@local' });
      setStatus('authenticated');
      return;
    }
    const stored = sessionStorage.getItem(REFRESH_TOKEN_KEY);
    if (!stored) { setStatus('unauthenticated'); return; }
    refreshTokens(stored)
      .then(({ idToken, refreshToken, expiresIn }) => applyTokens(idToken, refreshToken, expiresIn))
      .catch(() => { sessionStorage.removeItem(REFRESH_TOKEN_KEY); setStatus('unauthenticated'); });
  }, []); // applyTokens identity is stable across renders (refs inside useTokenManagement)

  const value: AuthContextValue = {
    status,
    actor,
    signIn: () => redirectToSignIn(),
    signOut: buildSignOut(),
    setTokens: (idToken, refreshToken, expiresIn) => applyTokens(idToken, refreshToken, expiresIn),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
